import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, ClipboardList, Target, Plus, X, Menu, Download,
  Award, TrendingUp, Clock, MessageSquare, FileText, HelpCircle, Settings, Calendar, Video,
  CheckSquare, DollarSign, Check, XCircle
} from 'lucide-react';
import ClassScheduleManagement from './ClassScheduleManagement';
import CoachClassRecords from './CoachClassRecords';

export default function CoachDashboard({ user, setUser }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/coach-login');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-0'} md:w-64 bg-gradient-to-b from-blue-800 to-purple-800 text-white flex flex-col transition-all duration-300 fixed md:relative h-full z-30 overflow-hidden`}>
        <div className="p-6 border-b border-white/20 flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-bold">教練介面</h2>
            <p className="text-sm text-white/80 mt-1">{user?.username}</p>
          </div>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 hover:bg-white/10 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <TabButton 
            icon={Target} 
            label="數據儀表板" 
            active={activeTab === 'dashboard'} 
            onClick={() => {
              setActiveTab('dashboard');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={Users} 
            label="學生管理" 
            active={activeTab === 'students'} 
            onClick={() => {
              setActiveTab('students');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={FileText} 
            label="課堂記錄" 
            active={activeTab === 'records'} 
            onClick={() => {
              setActiveTab('records');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={HelpCircle} 
            label="學生提問" 
            active={activeTab === 'questions'} 
            onClick={() => {
              setActiveTab('questions');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={ClipboardList} 
            label="任務模板" 
            active={activeTab === 'templates'} 
            onClick={() => {
              setActiveTab('templates');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={Calendar} 
            label="課時紀錄" 
            active={activeTab === 'schedule'} 
            onClick={() => {
              setActiveTab('schedule');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={Video} 
            label="學生課堂紀錄" 
            active={activeTab === 'class-records'} 
            onClick={() => {
              setActiveTab('class-records');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={MessageSquare} 
            label="待辦事項" 
            active={activeTab === 'pending'} 
            onClick={() => {
              setActiveTab('pending');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={CheckSquare} 
            label="出席管理" 
            active={activeTab === 'attendance'} 
            onClick={() => {
              setActiveTab('attendance');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={DollarSign} 
            label="費用管理" 
            active={activeTab === 'fees'} 
            onClick={() => {
              setActiveTab('fees');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
          <TabButton 
            icon={Settings} 
            label="個人設定" 
            active={activeTab === 'settings'} 
            onClick={() => {
              setActiveTab('settings');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} 
          />
        </nav>

        <div className="p-4 border-t border-white/20">
          <Button onClick={handleLogout} className="w-full bg-red-600 hover:bg-red-700">
            登出
          </Button>
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        {/* Mobile menu button - 改為左上角，添加 padding 避免擋住內容 */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden fixed top-4 left-4 bg-blue-800 text-white p-3 rounded-lg shadow-lg z-20 hover:bg-blue-700 transition-colors"
          >
            <Menu size={20} />
          </button>
        )}

        {/* 添加頂部 padding 避免被漢堡按鈕擋住 */}
        <div className={`${!sidebarOpen ? 'pt-16 md:pt-0' : ''}`}>
          {activeTab === 'dashboard' && <DashboardTab user={user} setActiveTab={setActiveTab} />}
          {activeTab === 'students' && <StudentsTab user={user} />}
          {activeTab === 'templates' && <TemplatesTab user={user} />}
          {activeTab === 'schedule' && <ClassScheduleManagement user={user} />}
          {activeTab === 'class-records' && <CoachClassRecords user={user} />}
          {activeTab === 'pending' && <PendingTab user={user} />}
          {activeTab === 'records' && <RecordsTab user={user} />}
          {activeTab === 'questions' && <QuestionsTab user={user} />}
          {activeTab === 'attendance' && <AttendanceTab user={user} />}
          {activeTab === 'fees' && <FeesTab user={user} />}
          {activeTab === 'settings' && <SettingsTab user={user} />}
        </div>
      </div>
    </div>
  );
}

function TabButton({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-sm md:text-base ${
        active ? 'bg-white/20' : 'hover:bg-white/10'
      }`}
    >
      <Icon size={20} />
      <span>{label}</span>
    </button>
  );
}

function DashboardTab({ user, setActiveTab }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('獲取統計數據失敗:', error);
      toast.error('獲取統計數據失敗');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">加載中...</div>;
  }

  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">數據儀表板</h1>

      {/* 統計卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <StatsCard
          icon={Users}
          label="負責學生"
          value={stats?.total_students || 0}
          color="blue"
          onClick={() => setActiveTab('students')}
        />
        <StatsCard
          icon={TrendingUp}
          label="任務完成率"
          value={`${stats?.task_completion_rate || 0}%`}
          color="green"
          onClick={() => setActiveTab('templates')}
        />
        <StatsCard
          icon={Award}
          label="平均積分"
          value={stats?.average_points || 0}
          color="purple"
          onClick={() => setActiveTab('students')}
        />
        <StatsCard
          icon={ClipboardList}
          label="待批改記錄"
          value={stats?.pending_reviews || 0}
          color="orange"
          onClick={() => setActiveTab('records')}
        />
        <StatsCard
          icon={MessageSquare}
          label="待回覆問題"
          value={stats?.pending_questions || 0}
          color="red"
          onClick={() => setActiveTab('questions')}
        />
        <StatsCard
          icon={Award}
          label="總獎項數"
          value={stats?.total_awards || 0}
          color="yellow"
          onClick={() => setActiveTab('students')}
        />
      </div>

      {/* 快速操作 */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold mb-4">快速操作</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button 
            onClick={() => setActiveTab('templates')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={20} className="mr-2" />
            創建任務
          </Button>
          <Button 
            onClick={() => setActiveTab('students')}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download size={20} className="mr-2" />
            導出報表
          </Button>
          <Button 
            onClick={() => setActiveTab('students')}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Users size={20} className="mr-2" />
            查看學生
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatsCard({ icon: Icon, label, value, color, onClick }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
  };

  return (
    <div 
      className={`p-6 rounded-lg border-2 ${colorClasses[color]} cursor-pointer hover:shadow-lg transition-shadow`}
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        <Icon size={32} />
        <div>
          <p className="text-sm opacity-80">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
      </div>
    </div>
  );
}

function StudentsTab({ user }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('獲取學生列表失敗:', error);
      toast.error('獲取學生列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (studentId, format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/reports/student-progress`,
        { student_id: studentId, format: format },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `progress_report_${studentId}.${format === 'word' ? 'docx' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('報表生成成功！');
    } catch (error) {
      console.error('生成報表失敗:', error);
      toast.error('生成報表失敗');
    }
  };

  const sortedStudents = [...students].sort((a, b) => {
    switch (sortBy) {
      case 'progress':
        return (b.task_completion_rate || 0) - (a.task_completion_rate || 0);
      case 'points':
        return (b.points || 0) - (a.points || 0);
      case 'activity':
        return (b.latest_activity || '').localeCompare(a.latest_activity || '');
      default:
        return (a.username || '').localeCompare(b.username || '');
    }
  });

  if (loading) {
    return <div className="p-8 text-center">加載中...</div>;
  }

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">學生管理</h1>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">排序：</label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">姓名</SelectItem>
              <SelectItem value="progress">完成率</SelectItem>
              <SelectItem value="points">積分</SelectItem>
              <SelectItem value="activity">最後活動</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {sortedStudents.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <p className="text-gray-500">暫無學生</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">學生</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">積分</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">完成率</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">獎項</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">最後活動</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedStudents.map((student) => (
                  <tr key={student.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img 
                          src={student.avatar_url} 
                          alt={student.username}
                          className="w-10 h-10 rounded-full"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{student.username}</p>
                          <p className="text-sm text-gray-500">{student.student_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{student.points}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full"
                            style={{ width: `${student.task_completion_rate}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          {student.task_completion_rate.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900">{student.awards_count}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {student.latest_activity 
                        ? new Date(student.latest_activity).toLocaleDateString()
                        : '無記錄'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button 
                          size="sm"
                          onClick={() => generateReport(student.user_id, 'word')}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Word報表
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => generateReport(student.user_id, 'excel')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Excel報表
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplatesTab({ user }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    task_type: 'custom',
    points: 1
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/task-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('獲取模板失敗:', error);
      toast.error('獲取模板失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/coach/task-templates`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('模板創建成功！');
      setDialogOpen(false);
      setFormData({ name: '', description: '', task_type: 'custom', points: 1 });
      fetchTemplates();
    } catch (error) {
      console.error('創建模板失敗:', error);
      toast.error('創建模板失敗');
    }
  };

  if (loading) {
    return <div className="p-8 text-center">加載中...</div>;
  }

  return (
    <div className="p-4 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">任務模板管理</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus size={20} className="mr-2" />
              創建模板
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>創建任務模板</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">模板名稱</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">任務描述</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">任務類型</label>
                <Select value={formData.task_type} onValueChange={(value) => setFormData({ ...formData, task_type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">力量訓練</SelectItem>
                    <SelectItem value="stretching">拉伸練習</SelectItem>
                    <SelectItem value="jumping">跳躍訓練</SelectItem>
                    <SelectItem value="custom">自訂</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">積分</label>
                <Input
                  type="number"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                創建模板
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {templates.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <p className="text-gray-500">暫無任務模板</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div key={template.template_id} className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
              <p className="text-gray-600 mb-4">{template.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">積分: {template.points}</span>
                <Button size="sm" className="bg-green-600 hover:bg-green-700">
                  使用模板
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PendingTab({ user }) {
  const [pending, setPending] = useState({ pending_records: [], pending_questions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPending();
  }, []);

  const fetchPending = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/pending-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPending(response.data);
    } catch (error) {
      console.error('獲取待辦事項失敗:', error);
      toast.error('獲取待辦事項失敗');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">加載中...</div>;
  }

  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">待辦事項</h1>

      <div className="space-y-6">
        {/* 待批改記錄 */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <ClipboardList size={24} className="text-orange-600" />
            待批改課堂記錄 ({pending.pending_records.length})
          </h2>
          {pending.pending_records.length === 0 ? (
            <p className="text-gray-500">暫無待批改記錄</p>
          ) : (
            <div className="space-y-3">
              {pending.pending_records.map((record) => (
                <div key={record.record_id} className="border p-4 rounded-lg">
                  <p className="font-medium">學生ID: {record.user_id}</p>
                  <p className="text-sm text-gray-600">創建時間: {new Date(record.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 待回覆問題 */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <MessageSquare size={24} className="text-blue-600" />
            待回覆技術問題 ({pending.pending_questions.length})
          </h2>
          {pending.pending_questions.length === 0 ? (
            <p className="text-gray-500">暫無待回覆問題</p>
          ) : (
            <div className="space-y-3">
              {pending.pending_questions.map((question) => (
                <div key={question.question_id} className="border p-4 rounded-lg">
                  <p className="font-medium mb-2">{question.content}</p>
                  <p className="text-sm text-gray-600">
                    提問時間: {new Date(question.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 出席管理標籤
function AttendanceTab({ user }) {
  const [weekStart, setWeekStart] = useState('');
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null); // 選中的時間段
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportMonth, setExportMonth] = useState('');
  const [exporting, setExporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const DAYS_OF_WEEK = ['週一', '週二', '週三', '週四', '週五', '週六', '週日'];

  useEffect(() => {
    // 設置當前週的開始日期（週一）
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    setWeekStart(monday.toISOString().split('T')[0]);
    // 設置當前日期為選中的日期
    setSelectedDay(dayOfWeek === 0 ? 6 : dayOfWeek - 1);
    // 設置當前月份為預設匯出月份
    setExportMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
  }, []);

  useEffect(() => {
    if (weekStart) {
      fetchWeeklyAttendance();
    }
  }, [weekStart]);

  const fetchWeeklyAttendance = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/attendance/weekly?week_start=${weekStart}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceData(response.data);
    } catch (error) {
      console.error('獲取出席記錄失敗:', error);
      toast.error('獲取出席記錄失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceChange = async (studentId, status, timeSlot = null) => {
    try {
      const token = localStorage.getItem('token');
      const dayData = attendanceData?.daily_attendance?.[selectedDay];
      if (!dayData) return;

      await axios.post(`${API}/coach/attendance`, {
        student_id: studentId,
        date: dayData.date,
        status: status,
        time_slot: timeSlot || selectedTimeSlot || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 更新本地狀態
      setAttendanceData(prev => {
        const updated = { ...prev };
        // 更新 time_slots 中的狀態
        if (updated.daily_attendance[selectedDay].time_slots) {
          updated.daily_attendance[selectedDay].time_slots = updated.daily_attendance[selectedDay].time_slots.map(slot => {
            if (timeSlot && slot.time_slot !== timeSlot) return slot;
            return {
              ...slot,
              students: slot.students.map(s => 
                s.student.user_id === studentId ? { ...s, status } : s
              )
            };
          });
        }
        // 同時更新 students 列表（向後兼容）
        if (updated.daily_attendance[selectedDay].students) {
          updated.daily_attendance[selectedDay].students = updated.daily_attendance[selectedDay].students.map(s =>
            s.student.user_id === studentId && (!timeSlot || s.time_slot === timeSlot) ? { ...s, status } : s
          );
        }
        return { ...updated };
      });

      toast.success('出席記錄已更新');
    } catch (error) {
      console.error('更新出席記錄失敗:', error);
      toast.error('更新出席記錄失敗');
    }
  };

  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const getDateForDay = (dayIndex) => {
    if (!weekStart) return '';
    const date = new Date(weekStart);
    date.setDate(date.getDate() + dayIndex);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const getCurrentDayStudents = () => {
    return attendanceData?.daily_attendance?.[selectedDay]?.students || [];
  };

  // 獲取當日的時間段列表
  const getCurrentDayTimeSlots = () => {
    return attendanceData?.daily_attendance?.[selectedDay]?.time_slots || [];
  };

  const getTotalStats = () => {
    if (!attendanceData?.daily_attendance) return { present: 0, absent: 0, total: 0 };
    
    let present = 0, absent = 0, total = 0;
    Object.values(attendanceData.daily_attendance).forEach(day => {
      day.students.forEach(s => {
        total++;
        if (s.status === 'present') present++;
        if (s.status === 'absent') absent++;
      });
    });
    return { present, absent, total };
  };

  // 預覽報表數據
  const handlePreviewReport = async () => {
    if (!exportMonth) {
      toast.error('請選擇月份');
      return;
    }
    
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/attendance/export?month=${exportMonth}&format=json`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(response.data);
    } catch (error) {
      console.error('獲取報表數據失敗:', error);
      toast.error('獲取報表數據失敗');
    } finally {
      setExporting(false);
    }
  };

  // 下載 Excel 報表
  const handleDownloadExcel = async () => {
    if (!exportMonth) {
      toast.error('請選擇月份');
      return;
    }
    
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/attendance/export?month=${exportMonth}&format=excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // 創建下載連結
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `考勤報表_${exportMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('報表下載成功');
      setExportDialogOpen(false);
    } catch (error) {
      console.error('下載報表失敗:', error);
      toast.error('下載報表失敗');
    } finally {
      setExporting(false);
    }
  };

  // 匯出按鈕組件
  const ExportReportButton = () => (
    <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
          data-testid="export-report-btn"
        >
          <Download size={16} className="mr-1" />
          匯出報表
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>匯出月度考勤報表</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* 月份選擇 */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">選擇月份:</label>
            <Input
              type="month"
              value={exportMonth}
              onChange={(e) => {
                setExportMonth(e.target.value);
                setPreviewData(null);
              }}
              className="w-full md:w-48"
              data-testid="export-month-input"
            />
            <Button 
              variant="outline" 
              onClick={handlePreviewReport}
              disabled={exporting}
              data-testid="preview-report-btn"
            >
              {exporting ? '載入中...' : '預覽報表'}
            </Button>
          </div>
          
          {/* 預覽數據 */}
          {previewData && (
            <div className="mt-4 space-y-4">
              {/* 統計摘要 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-blue-600">總學生數</p>
                  <p className="text-xl font-bold text-blue-700">{previewData.total_students}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-purple-600">總課堂數</p>
                  <p className="text-xl font-bold text-purple-700">{previewData.total_classes}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-green-600">總出席</p>
                  <p className="text-xl font-bold text-green-700">{previewData.total_present}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg text-center">
                  <p className="text-xs text-orange-600">出席率</p>
                  <p className="text-xl font-bold text-orange-700">{previewData.attendance_rate}%</p>
                </div>
              </div>
              
              {/* 學生列表 */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <h3 className="font-medium text-gray-700">學生出席詳情</h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {previewData.students.length === 0 ? (
                    <p className="p-4 text-center text-gray-500">此月份無出席記錄</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">學生姓名</th>
                          <th className="px-3 py-2 text-center">應到</th>
                          <th className="px-3 py-2 text-center">出席</th>
                          <th className="px-3 py-2 text-center">缺席</th>
                          <th className="px-3 py-2 text-center">出席率</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {previewData.students.map((student, index) => {
                          const rate = student.total_classes > 0 
                            ? Math.round(student.present / student.total_classes * 100) 
                            : 0;
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2">{student.name}</td>
                              <td className="px-3 py-2 text-center">{student.total_classes}</td>
                              <td className="px-3 py-2 text-center text-green-600">{student.present}</td>
                              <td className="px-3 py-2 text-center text-red-600">{student.absent}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  rate >= 80 ? 'bg-green-100 text-green-700' :
                                  rate >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {rate}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* 下載按鈕 */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setExportDialogOpen(false)}
            >
              取消
            </Button>
            <Button 
              onClick={handleDownloadExcel}
              disabled={exporting || !exportMonth}
              className="bg-green-600 hover:bg-green-700"
              data-testid="download-excel-btn"
            >
              <Download size={16} className="mr-2" />
              {exporting ? '下載中...' : '下載 Excel 報表'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading && !attendanceData) {
    return <div className="p-8 text-center">加載中...</div>;
  }

  const stats = getTotalStats();
  const currentDayStudents = getCurrentDayStudents();
  const currentDayTimeSlots = getCurrentDayTimeSlots();

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">出席管理</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
            上一週
          </Button>
          <span className="px-3 py-2 bg-blue-50 rounded-lg font-medium text-blue-700 text-sm">
            {weekStart} ~ {attendanceData?.week_end || ''}
          </span>
          <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
            下一週
          </Button>
          <ExportReportButton />
        </div>
      </div>

      {/* 沒有課程排程的提示 */}
      {attendanceData && !attendanceData.has_schedules && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800">
            尚未設定課程排程。請先在「課時紀錄」中添加課程，設定每天的學生名單。
          </p>
        </div>
      )}

      {/* 日期選擇器 - 手機橫向滾動，桌面網格 */}
      <div className="overflow-x-auto pb-2 mb-6">
        <div className="flex md:grid md:grid-cols-7 gap-2 min-w-max md:min-w-0">
          {DAYS_OF_WEEK.map((day, index) => {
            const dayData = attendanceData?.daily_attendance?.[index];
            const timeSlots = dayData?.time_slots || [];
            const totalStudents = timeSlots.reduce((sum, slot) => sum + slot.students.length, 0);
            const hasStudents = totalStudents > 0;
            return (
              <button
                key={index}
                onClick={() => {
                  setSelectedDay(index);
                  setSelectedTimeSlot(null);
                }}
                className={`p-2 md:p-3 rounded-lg text-center transition-all min-w-[70px] md:min-w-0 ${
                  selectedDay === index
                    ? 'bg-blue-600 text-white shadow-lg'
                    : hasStudents
                      ? 'bg-white hover:bg-blue-50 border-2 border-blue-200'
                      : 'bg-gray-100 text-gray-400 border border-gray-200'
                }`}
                data-testid={`day-selector-${index}`}
              >
                <div className="font-semibold text-sm md:text-base">{day}</div>
                <div className={`text-xs ${selectedDay === index ? 'text-blue-100' : 'text-gray-500'}`}>
                  {getDateForDay(index)}
                </div>
                <div className={`text-xs md:text-sm mt-1 ${selectedDay === index ? 'text-blue-100' : 'text-gray-600'}`}>
                  {totalStudents} 人
                </div>
                {timeSlots.length > 1 && (
                  <div className={`text-xs ${selectedDay === index ? 'text-blue-200' : 'text-gray-400'}`}>
                    {timeSlots.length} 堂課
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* 當日課程 - 按時間段分組 */}
      {currentDayTimeSlots.length > 0 ? (
        <div className="space-y-4">
          {currentDayTimeSlots.map((timeSlotData, slotIndex) => (
            <div key={slotIndex} className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* 時間段標題 */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3 md:p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
                      <Clock size={18} />
                      {timeSlotData.time_slot || '未指定時間'}
                    </h2>
                    <p className="text-xs md:text-sm text-blue-100 mt-1">
                      {DAYS_OF_WEEK[selectedDay]} ({getDateForDay(selectedDay)})
                      {timeSlotData.location && ` · ${timeSlotData.location}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{timeSlotData.students.length}</p>
                    <p className="text-xs text-blue-100">位學生</p>
                  </div>
                </div>
              </div>

              {/* 學生列表 */}
              <div className="divide-y divide-gray-200" data-testid={`attendance-list-${slotIndex}`}>
                {timeSlotData.students.map((studentData) => (
                  <div 
                    key={`${studentData.student.user_id}-${slotIndex}`}
                    className="p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 md:gap-4">
                      <img 
                        src={studentData.student.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(studentData.student.student_name || studentData.student.username)}&background=random`}
                        alt={studentData.student.username}
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full"
                      />
                      <div>
                        <p className="font-medium text-gray-900 text-sm md:text-base">
                          {studentData.student.student_name || studentData.student.username}
                        </p>
                        <p className="text-xs md:text-sm text-gray-500">
                          {studentData.status === 'present' && '✓ 已出席'}
                          {studentData.status === 'absent' && '✗ 已缺席'}
                          {!studentData.status && '尚未點名'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-13 md:ml-0">
                      <Button
                        size="sm"
                        onClick={() => handleAttendanceChange(studentData.student.user_id, 'present', timeSlotData.time_slot)}
                        className={`flex-1 md:flex-none ${
                          studentData.status === 'present'
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-gray-200 hover:bg-green-500 text-gray-700 hover:text-white'
                        }`}
                        data-testid={`mark-present-${studentData.student.user_id}-${slotIndex}`}
                      >
                        <Check size={16} className="mr-1" />
                        出席
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleAttendanceChange(studentData.student.user_id, 'absent', timeSlotData.time_slot)}
                        className={`flex-1 md:flex-none ${
                          studentData.status === 'absent'
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-gray-200 hover:bg-red-500 text-gray-700 hover:text-white'
                        }`}
                        data-testid={`mark-absent-${studentData.student.user_id}-${slotIndex}`}
                      >
                        <XCircle size={16} className="mr-1" />
                        缺席
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 text-white">
            <h2 className="text-lg font-semibold">
              {DAYS_OF_WEEK[selectedDay]} ({getDateForDay(selectedDay)}) - 應到學生
            </h2>
            <p className="text-sm text-blue-100">共 {currentDayStudents.length} 位學生</p>
          </div>
          <div className="p-8 text-center">
            <p className="text-gray-500">今日無排定課程學生</p>
            <p className="text-sm text-gray-400 mt-2">請先在「課時紀錄」中添加此日的課程</p>
          </div>
        </div>
      )}

      {/* 統計摘要 */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-3">
            <Check className="text-green-600" size={24} />
            <div>
              <p className="text-sm text-green-700">本週出席</p>
              <p className="text-2xl font-bold text-green-800">{stats.present}</p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center gap-3">
            <XCircle className="text-red-600" size={24} />
            <div>
              <p className="text-sm text-red-700">本週缺席</p>
              <p className="text-2xl font-bold text-red-800">{stats.absent}</p>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center gap-3">
            <Users className="text-blue-600" size={24} />
            <div>
              <p className="text-sm text-blue-700">本週應到人次</p>
              <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 費用管理標籤
function FeesTab({ user }) {
  const [students, setStudents] = useState([]);
  const [incomeData, setIncomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingStudent, setEditingStudent] = useState(null);
  const [feeInput, setFeeInput] = useState('');

  useEffect(() => {
    fetchStudentsFees();
    fetchIncomeProjection();
  }, []);

  const fetchStudentsFees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/students/fees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('獲取學生費用失敗:', error);
      toast.error('獲取學生費用失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomeProjection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/income/projection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncomeData(response.data);
    } catch (error) {
      console.error('獲取收入預估失敗:', error);
    }
  };

  const handleUpdateFee = async (studentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/coach/students/${studentId}/fee`, {
        monthly_fee: parseFloat(feeInput) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('費用已更新');
      setEditingStudent(null);
      setFeeInput('');
      fetchStudentsFees();
      fetchIncomeProjection();
    } catch (error) {
      console.error('更新費用失敗:', error);
      toast.error('更新費用失敗');
    }
  };

  const handleTogglePaid = async (studentId, currentPaidStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/coach/students/${studentId}/fee`, {
        fee_paid: !currentPaidStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(!currentPaidStatus ? '已標記為已繳費' : '已標記為未繳費');
      fetchStudentsFees();
      fetchIncomeProjection();
    } catch (error) {
      console.error('更新繳費狀態失敗:', error);
      toast.error('更新繳費狀態失敗');
    }
  };

  if (loading) {
    return <div className="p-8 text-center">加載中...</div>;
  }

  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">費用管理</h1>

      {/* 收入統計卡片 */}
      {incomeData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 md:p-6 rounded-lg text-white">
            <p className="text-xs md:text-sm opacity-80">預估月收入</p>
            <p className="text-xl md:text-3xl font-bold mt-1" data-testid="projected-income">
              ${incomeData.total_projected_income.toLocaleString()}
            </p>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 md:p-6 rounded-lg text-white">
            <p className="text-xs md:text-sm opacity-80">已收取</p>
            <p className="text-xl md:text-3xl font-bold mt-1" data-testid="collected-income">
              ${incomeData.total_collected.toLocaleString()}
            </p>
          </div>
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 md:p-6 rounded-lg text-white">
            <p className="text-xs md:text-sm opacity-80">待收取</p>
            <p className="text-xl md:text-3xl font-bold mt-1" data-testid="pending-income">
              ${incomeData.total_pending.toLocaleString()}
            </p>
          </div>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 md:p-6 rounded-lg text-white">
            <p className="text-xs md:text-sm opacity-80">學生數</p>
            <p className="text-xl md:text-3xl font-bold mt-1">
              {incomeData.students_paid_count} / {incomeData.total_students}
            </p>
            <p className="text-xs opacity-70 mt-1">已繳 / 總計</p>
          </div>
        </div>
      )}

      {/* 學生費用列表 */}
      {students.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <p className="text-gray-500">暫無學生</p>
        </div>
      ) : (
        <>
          {/* 桌面版表格 */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="fees-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">學生</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">月費金額</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">繳費狀態</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">繳費日期</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {students.map((student) => (
                    <tr key={student.user_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img 
                            src={student.avatar_url} 
                            alt={student.username}
                            className="w-10 h-10 rounded-full"
                          />
                          <div>
                            <p className="font-medium text-gray-900">
                              {student.student_name || student.username}
                            </p>
                            <p className="text-sm text-gray-500">{student.student_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {editingStudent === student.user_id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={feeInput}
                              onChange={(e) => setFeeInput(e.target.value)}
                              placeholder="輸入金額"
                              className="w-32"
                              autoFocus
                            />
                            <Button 
                              size="sm" 
                              onClick={() => handleUpdateFee(student.user_id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              儲存
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setEditingStudent(null);
                                setFeeInput('');
                              }}
                            >
                              取消
                            </Button>
                          </div>
                        ) : (
                          <span className="font-medium text-gray-900">
                            ${(student.monthly_fee || 0).toLocaleString()}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {student.fee_paid ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                            <Check size={14} />
                            已繳費
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                            <Clock size={14} />
                            未繳費
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {student.fee_paid_date 
                          ? new Date(student.fee_paid_date).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingStudent(student.user_id);
                              setFeeInput(student.monthly_fee?.toString() || '');
                            }}
                            data-testid={`edit-fee-${student.user_id}`}
                          >
                            編輯金額
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleTogglePaid(student.user_id, student.fee_paid)}
                            className={student.fee_paid 
                              ? 'bg-orange-500 hover:bg-orange-600' 
                              : 'bg-green-600 hover:bg-green-700'
                            }
                            data-testid={`toggle-paid-${student.user_id}`}
                          >
                            {student.fee_paid ? '標記未繳' : '標記已繳'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 手機版卡片列表 */}
          <div className="md:hidden space-y-3">
            {students.map((student) => (
              <div key={student.user_id} className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <img 
                      src={student.avatar_url} 
                      alt={student.username}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        {student.student_name || student.username}
                      </p>
                      <p className="text-xs text-gray-500">{student.student_id}</p>
                    </div>
                  </div>
                  {student.fee_paid ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <Check size={12} />
                      已繳
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                      <Clock size={12} />
                      未繳
                    </span>
                  )}
                </div>
                
                <div className="flex items-center justify-between mb-3 text-sm">
                  <div>
                    <span className="text-gray-500">月費：</span>
                    {editingStudent === student.user_id ? (
                      <div className="inline-flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          value={feeInput}
                          onChange={(e) => setFeeInput(e.target.value)}
                          placeholder="金額"
                          className="w-24 h-8 text-sm"
                          autoFocus
                        />
                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateFee(student.user_id)}
                          className="bg-green-600 hover:bg-green-700 h-8 px-2"
                        >
                          ✓
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingStudent(null);
                            setFeeInput('');
                          }}
                          className="h-8 px-2"
                        >
                          ✗
                        </Button>
                      </div>
                    ) : (
                      <span className="font-semibold text-gray-900">
                        ${(student.monthly_fee || 0).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {student.fee_paid_date && (
                    <div className="text-xs text-gray-500">
                      繳費日: {new Date(student.fee_paid_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingStudent(student.user_id);
                      setFeeInput(student.monthly_fee?.toString() || '');
                    }}
                    className="flex-1 text-xs"
                    data-testid={`edit-fee-mobile-${student.user_id}`}
                  >
                    編輯金額
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleTogglePaid(student.user_id, student.fee_paid)}
                    className={`flex-1 text-xs ${student.fee_paid 
                      ? 'bg-orange-500 hover:bg-orange-600' 
                      : 'bg-green-600 hover:bg-green-700'
                    }`}
                    data-testid={`toggle-paid-mobile-${student.user_id}`}
                  >
                    {student.fee_paid ? '標記未繳' : '標記已繳'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
// 課堂記錄批改標籤
function RecordsTab({ user }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, reviewed

  useEffect(() => {
    fetchRecords();
  }, [filter]);

  const fetchRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      // 獲取待批改記錄
      const response = await axios.get(`${API}/coach/pending-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let recordsList = response.data.pending_records || [];
      
      // 根據篩選器過濾
      if (filter === 'pending') {
        recordsList = recordsList.filter(r => !r.reviewed);
      } else if (filter === 'reviewed') {
        recordsList = recordsList.filter(r => r.reviewed);
      }
      
      setRecords(recordsList);
    } catch (error) {
      console.error('獲取課堂記錄失敗:', error);
      toast.error('獲取課堂記錄失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async (recordId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/class-records/${recordId}/review`,
        { reviewed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('已標記為已批改');
      fetchRecords();
    } catch (error) {
      console.error('標記失敗:', error);
      toast.error('標記失敗');
    }
  };

  if (loading) {
    return <div className="p-8 text-center">加載中...</div>;
  }

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">課堂記錄批改</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部記錄</SelectItem>
            <SelectItem value="pending">待批改</SelectItem>
            <SelectItem value="reviewed">已批改</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {records.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <p className="text-gray-500">暫無課堂記錄</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {records.map((record) => (
            <div key={record.record_id} className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    學生 ID: {record.user_id}
                  </h3>
                  <p className="text-sm text-gray-600">
                    上傳時間: {new Date(record.created_at).toLocaleString()}
                  </p>
                  {record.location && (
                    <p className="text-sm text-blue-600">📍 {record.location}</p>
                  )}
                </div>
                {!record.reviewed && (
                  <Button
                    size="sm"
                    onClick={() => handleMarkReviewed(record.record_id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    標記已批改
                  </Button>
                )}
                {record.reviewed && (
                  <span className="text-green-600 font-medium">✓ 已批改</span>
                )}
              </div>
              
              {/* 顯示影片 */}
              {record.video_urls && record.video_urls.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">課堂影片：</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {record.video_urls.map((videoUrl, index) => (
                      <video
                        key={index}
                        controls
                        className="w-full rounded-lg"
                        src={`${window.location.origin}${videoUrl}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 學生提問回覆標籤
function QuestionsTab({ user }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/coach/pending-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.pending_questions || []);
    } catch (error) {
      console.error('獲取問題失敗:', error);
      toast.error('獲取問題失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      toast.error('請輸入回覆內容');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/questions/${selectedQuestion.question_id}/reply`,
        { reply: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('回覆成功！');
      setReplyDialogOpen(false);
      setReplyText('');
      setSelectedQuestion(null);
      fetchQuestions();
    } catch (error) {
      console.error('回覆失敗:', error);
      toast.error('回覆失敗');
    }
  };

  if (loading) {
    return <div className="p-8 text-center">加載中...</div>;
  }

  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">學生提問</h1>

      {questions.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <p className="text-gray-500">暫無待回覆問題</p>
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map((question) => (
            <div key={question.question_id} className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {question.content}
                  </h3>
                  <p className="text-sm text-gray-600">
                    學生: {question.student_name || question.student_id}
                  </p>
                  <p className="text-sm text-gray-600">
                    提問時間: {new Date(question.created_at).toLocaleString()}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setSelectedQuestion(question);
                    setReplyDialogOpen(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  回覆
                </Button>
              </div>
              
              {/* 顯示圖片 */}
              {question.image_url && (
                <img
                  src={`${window.location.origin}${question.image_url}`}
                  alt="Question"
                  className="mt-4 rounded-lg max-w-md"
                />
              )}
              
              {/* 顯示影片 */}
              {question.video_url && (
                <video
                  controls
                  className="mt-4 rounded-lg max-w-md"
                  src={`${window.location.origin}${question.video_url}`}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* 回覆對話框 */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>回覆學生提問</DialogTitle>
          </DialogHeader>
          {selectedQuestion && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">{selectedQuestion.content}</p>
              </div>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="請輸入您的回覆..."
                rows={6}
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setReplyDialogOpen(false);
                    setReplyText('');
                    setSelectedQuestion(null);
                  }}
                >
                  取消
                </Button>
                <Button onClick={handleReply} className="bg-blue-600 hover:bg-blue-700">
                  發送回覆
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// 個人設定標籤
function SettingsTab({ user }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('新密碼與確認密碼不一致');
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error('新密碼至少需要6個字符');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/users/${user.user_id}/password`,
        {
          old_password: formData.currentPassword,
          new_password: formData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('密碼修改成功！');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('修改密碼失敗:', error);
      toast.error(error.response?.data?.detail || '修改密碼失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">個人設定</h1>

      {/* 個人資料卡片 */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
        <h2 className="text-xl font-semibold mb-4">個人資料</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <img 
              src={user.avatar_url} 
              alt={user.username}
              className="w-16 h-16 rounded-full"
            />
            <div>
              <p className="font-medium text-gray-900">{user.coach_name || user.username}</p>
              <p className="text-sm text-gray-600">{user.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-gray-600">帳號</p>
              <p className="font-medium text-gray-900">{user.username}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">電話</p>
              <p className="font-medium text-gray-900">{user.phone}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">機構編號</p>
              <p className="font-medium text-gray-900">{user.organization_id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">加入日期</p>
              <p className="font-medium text-gray-900">
                {new Date(user.join_date).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 修改密碼卡片 */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold mb-4">修改密碼</h2>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium mb-2">當前密碼</label>
            <Input
              type="password"
              value={formData.currentPassword}
              onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">新密碼</label>
            <Input
              type="password"
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">確認新密碼</label>
            <Input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? '處理中...' : '修改密碼'}
          </Button>
        </form>
      </div>
    </div>
  );
}

